import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import {
  Stack,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";

import ServicesApi from "../../Classes/API/Services/ServicesApi";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";

export default function ManageServices() {
  const [serviceName, setServiceName] = useState("");
  const [servicePrice, setServicePrice] = useState("");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // States for Edit and Delete
  const [editModal, setEditModal] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [newPrice, setNewPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const req = {
        name: searchTerm,
        pagingParams: {
          pageNumber: pageNumber,
          pageSize: 5,
        },
      };
      const res = await ServicesApi.GetAllServices(req);
      if (res.status === "Success") {
        setServices(res.data.list || []);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    }
    setLoading(false);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchServices();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [pageNumber, searchTerm]);

  const handleAddService = async () => {
    if (!serviceName || !servicePrice) {
      return toast.error("Please fill in all fields");
    }
    setSubmitting(true);
    try {
      const res = await ServicesApi.CreateService({
        name: serviceName,
        price: parseFloat(servicePrice),
      });

      if (res.status === "Success" || res.Status === "Success") {
        toast.success("Service created!");
        setServiceName("");
        setServicePrice("");
        setPageNumber(1);
        fetchServices();
      }
    } catch (err) {
      toast.error("Failed to add service");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenEdit = (service) => {
    setSelectedService(service);
    setNewPrice(service.price);
    setEditModal(true);
  };

  const handleUpdatePrice = async () => {
    if (!newPrice || Number(newPrice) < 0) return toast.error("Invalid price");

    setSubmitting(true);
    const loadId = toast.loading("Updating price...");
    try {
      const res = await ServicesApi.UpdateServicePrice(
        selectedService.id,
        newPrice,
      );
      if (res.status === "Success" || res.Status === "Success") {
        toast.success("Price updated successfully!", { id: loadId });
        setEditModal(false);
        fetchServices();
      } else {
        toast.error(res.message || "Update failed", { id: loadId });
      }
    } catch (error) {
      toast.error("Error updating price", { id: loadId });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteService = (service) => {
    Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${service.name}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      confirmButtonText: "Yes, delete it!",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const loadId = toast.loading("Deleting...");
        try {
          const res = await ServicesApi.DeleteService(service.id);
          if (res?.status === "Success" || res?.Status === "Success") {
            toast.success("Deleted successfully", { id: loadId });
            fetchServices();
          } else {
            toast.error(res?.message || "Something went wrong", { id: loadId });
          }
        } catch (error) {
          toast.error("Server error", { id: loadId });
        }
      }
    });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pb={1} display="flex" flexDirection="column" gap={4}>
        {/* Create Service Card */}
        <Card sx={{ boxShadow: "0 8px 16px rgba(0,0,0,0.05)" }}>
          <MDBox p={3}>
            <MDTypography variant="h5" fontWeight="bold">
              Create New Service
            </MDTypography>
            <MDBox mt={3}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems="center"
              >
                <MDBox flex={2} width="100%">
                  <MDInput
                    fullWidth
                    label="Service Name"
                    value={serviceName}
                    onChange={(e) => setServiceName(e.target.value)}
                  />
                </MDBox>
                <MDBox flex={1} width="100%">
                  <MDInput
                    type="number"
                    fullWidth
                    label="Price (EGP)"
                    value={servicePrice}
                    onChange={(e) => setServicePrice(e.target.value)}
                  />
                </MDBox>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={handleAddService}
                  disabled={submitting}
                  sx={{ height: "44px" }}
                >
                  <Icon sx={{ mr: 1 }}>add</Icon> Save
                </MDButton>
              </Stack>
            </MDBox>
          </MDBox>
        </Card>

        {/* Services List Card */}
        <Card sx={{ boxShadow: "0 8px 16px rgba(0,0,0,0.05)" }}>
          <MDBox p={3}>
            <MDBox mb={2}>
              <MDInput
                fullWidth
                label="Search by name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNumber(1);
                }}
              />
            </MDBox>

            <MDBox sx={{ overflowX: "auto", position: "relative" }}>
              {loading && (
                <MDBox display="flex" justifyContent="center" p={2}>
                  <CircularProgress size={30} color="info" />
                </MDBox>
              )}

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid #f0f2f5" }}>
                    <th style={{ textAlign: "left", padding: "12px" }}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        color="secondary"
                      >
                        SERVICE NAME
                      </MDTypography>
                    </th>
                    <th style={{ textAlign: "left", padding: "12px" }}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        color="secondary"
                      >
                        CURRENT PRICE
                      </MDTypography>
                    </th>
                    <th style={{ textAlign: "right", padding: "12px" }}>
                      <MDTypography
                        variant="caption"
                        fontWeight="bold"
                        color="secondary"
                      >
                        ACTIONS
                      </MDTypography>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((service, index) => (
                    <tr
                      key={service.id}
                      style={{
                        borderBottom: "1px solid #f0f2f5",
                        backgroundColor: index % 2 === 0 ? "#f8f9fa" : "white",
                      }}
                    >
                      <td style={{ padding: "12px" }}>
                        <MDTypography variant="button" fontWeight="medium">
                          {service.name}
                        </MDTypography>
                      </td>
                      <td style={{ padding: "12px" }}>
                        <MDTypography variant="button" fontWeight="bold">
                          {service.price?.toLocaleString()} EGP
                        </MDTypography>
                      </td>
                      <td style={{ padding: "12px", textAlign: "right" }}>
                          <IconButton
                            color="info"
                            onClick={() => handleOpenEdit(service)}
                          >
                            <Icon>edit</Icon>
                          </IconButton>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteService(service)}
                          >
                            <Icon>delete</Icon>
                          </IconButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {services.length === 0 && !loading && (
                <MDBox py={5} textAlign="center">
                  <MDTypography variant="h6" color="text">
                    No services found.
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>

            {/* Pagination */}
            <MDBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={3}
              gap={2}
            >
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                iconOnly
                disabled={pageNumber === 1}
                onClick={() => setPageNumber((prev) => prev - 1)}
              >
                <Icon>chevron_left</Icon>
              </MDButton>
              <MDTypography variant="button" fontWeight="bold">
                Page {pageNumber} of {totalPages}
              </MDTypography>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                iconOnly
                disabled={pageNumber === totalPages}
                onClick={() => setPageNumber((prev) => prev + 1)}
              >
                <Icon>chevron_right</Icon>
              </MDButton>
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>

      {/* Edit Price Modal */}
      <Dialog
        open={editModal}
        onClose={() => !submitting && setEditModal(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Update Service Price</DialogTitle>
        <DialogContent>
          <MDBox p={1}>
            <MDTypography variant="button" color="text" mb={2} display="block">
              Update price for: <b>{selectedService?.name}</b>
            </MDTypography>
            <MDInput
              label="New Price (EGP)"
              type="number"
              fullWidth
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              disabled={submitting}
            />
            <MDTypography
              variant="caption"
              color="warning"
              sx={{ mt: 1, display: "block" }}
            >
              Note: This will only affect new bundles.
            </MDTypography>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <MDButton
            color="secondary"
            onClick={() => setEditModal(false)}
            disabled={submitting}
          >
            Cancel
          </MDButton>
          <MDButton
            color="info"
            variant="gradient"
            onClick={handleUpdatePrice}
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save Changes"
            )}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
